import { useRef, type ComponentType, type KeyboardEvent } from 'react'

export type SegmentedTabIcon = ComponentType<{
  size?: number
  className?: string
}>

export interface SegmentedTabItem<T extends string = string> {
  value: T
  label: string
  icon?: SegmentedTabIcon
  /** Optional native browser tooltip shown on hover. */
  title?: string
}

export interface SegmentedTabsProps<T extends string = string> {
  tabs: ReadonlyArray<SegmentedTabItem<T>>
  value: T
  onChange: (value: T) => void
  /** Lucide icon size in pixels. Defaults to 14. */
  iconSize?: number
  className?: string
  ariaLabel?: string
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

export const SegmentedTabs = <T extends string = string>({
  tabs,
  value,
  onChange,
  iconSize = 14,
  className,
  ariaLabel,
}: SegmentedTabsProps<T>) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    // Resolve current index from the active value at handler-call time so
    // arrow-key cycling continues working even if focus drifts behind the
    // logical selection (e.g., before this handler also moved focus).
    const currentIdx = tabs.findIndex((t) => t.value === value)
    if (currentIdx < 0) return

    let nextIdx: number | null = null
    if (e.key === 'ArrowRight') nextIdx = (currentIdx + 1) % tabs.length
    else if (e.key === 'ArrowLeft')
      nextIdx = (currentIdx - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') nextIdx = 0
    else if (e.key === 'End') nextIdx = tabs.length - 1

    if (nextIdx !== null) {
      e.preventDefault()
      onChange(tabs[nextIdx].value)
      // WAI-ARIA tabs: focus follows selection on arrow-key navigation.
      buttonRefs.current[nextIdx]?.focus()
    }
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={join(
        'inline-flex flex-nowrap gap-0 rounded-lg bg-gray-100 p-[3px]',
        className
      )}
    >
      {tabs.map(({ value: v, label, icon: Icon, title }, idx) => {
        const active = v === value
        return (
          <button
            key={v}
            ref={(el) => {
              buttonRefs.current[idx] = el
            }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            data-active={active ? 'true' : undefined}
            data-value={v}
            title={title}
            onClick={() => onChange(v)}
            onKeyDown={handleKeyDown}
            className={join(
              'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-500 focus-visible:ring-offset-1',
              active
                ? 'bg-white font-semibold text-doubleNickel-gray-900 shadow-sm'
                : 'bg-transparent text-doubleNickel-gray-500 hover:bg-white/60 hover:text-doubleNickel-gray-900'
            )}
          >
            {Icon ? <Icon size={iconSize} /> : null}
            {label}
          </button>
        )
      })}
    </div>
  )
}

export default SegmentedTabs
